import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionMenu,
  ConfirmationDialog,
  CopyableText,
  DataGrid,
  Dialog,
  Form,
  formatDate,
  FormButton,
  Input,
  SelectInput,
  StatusBadge,
  string,
  SwitchInput,
  useNotifications
} from '@ijoin/components';
import {
  useDeleteAdminAccountsRefLogin,
  useDeleteAdminAdvisoryOrganizationAdvisor,
  useDeleteAdminLogin,
  useGetAdminLogin,
  useGetAdminLoginAdvisoryOrganization,
  useGetAdminLoginPlanEnrollments,
  usePatchAdminAccountsRefLogin,
  usePatchAdminLoginDisableMfa,
  usePatchAdminLoginResetPassword,
  usePatchAdminLoginUnlock,
  usePostAdminAccountsRefLogin,
  usePostAdminLoginSendInvitation
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate
} from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import {
  EditDetailsDialog,
  EnableMfaDialog,
  ResetLinkDialog,
  ResetPasswordDialog
} from '../../components';
import { ACCOUNT_TYPES } from '../../consts';
import { getLoginType } from '../../helpers';
import { ApiError } from '../../types';

type LinkAccountFormValues = {
  accountId: string;
  accountTypeId: string;
  isAdmin: boolean;
};

const linkAccountDefaultValues: LinkAccountFormValues = {
  accountId: '',
  accountTypeId: ACCOUNT_TYPES.ADVISORS,
  isAdmin: false
};

const accountTypeOptions = [
  { label: 'Advisor', value: ACCOUNT_TYPES.ADVISORS },
  { label: 'Participant', value: ACCOUNT_TYPES.PARTICIPANTS },
  { label: 'Admin', value: ACCOUNT_TYPES.ADMIN }
];

const isLocked = (failedAttempts?: number, allowAccessAfter?: string) =>
  (failedAttempts ?? 0) >= 5 || !!allowAccessAfter;

const UserDetailPage = () => {
  const { loginId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const login = useGetAdminLogin(loginId);
  const notifications = useNotifications();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [`/api/v3/admin/logins/${loginId}`]
    });
  };

  const unlock = usePatchAdminLoginUnlock({
    mutation: {
      onError: () => notifications.error('Failed to unlock user'),
      onSuccess: () => {
        invalidate();
        notifications.success('User unlocked');
      }
    }
  });
  const disableMfa = usePatchAdminLoginDisableMfa({
    mutation: {
      onError: () => notifications.error('Failed to disable MFA'),
      onSuccess: () => {
        invalidate();
        notifications.success('MFA disabled');
      }
    }
  });
  const deleteLogin = useDeleteAdminLogin({
    mutation: {
      onError: () => notifications.error('Failed to delete login'),
      onSuccess: () => {
        notifications.success('Login deleted');
        navigate({ to: '/users' });
      }
    }
  });
  const deleteAdvisor = useDeleteAdminAdvisoryOrganizationAdvisor({
    mutation: {
      onError: () => notifications.error('Failed to delete advisor'),
      onSuccess: () => {
        notifications.success('Advisor deleted');
        invalidate();
      }
    }
  });

  const sendInvitation = usePostAdminLoginSendInvitation({
    mutation: {
      onError: () => notifications.error('Failed to send invitation'),
      onSuccess: () => notifications.success('Invitation sent')
    }
  });

  const resetPasswordEmail = usePatchAdminLoginResetPassword({
    mutation: {
      onError: () => notifications.error('Failed to send password reset email'),
      onSuccess: () => notifications.success('Password reset email sent')
    }
  });

  const [deletingOrg, setDeletingOrg] = useState<null | {
    advisorId: string;
    advisoryOrganizationId: string;
    name: string;
  }>(null);
  const [editDetailsOpen, , setEditDetailsOpen] = useToggle(false);
  const [resetPasswordOpen, , setResetPasswordOpen] = useToggle(false);
  const [resetLinkOpen, , setResetLinkOpen] = useToggle(false);
  const [resetEmailOpen, , setResetEmailOpen] = useToggle(false);
  const [enableMfaOpen, , setEnableMfaOpen] = useToggle(false);
  const [disableMfaOpen, , setDisableMfaOpen] = useToggle(false);
  const [deleteLoginOpen, , setDeleteLoginOpen] = useToggle(false);
  const [linkAccountOpen, , setLinkAccountOpen] = useToggle(false);
  const [unlinkingAccount, setUnlinkingAccount] = useState<null | {
    accountsRefLoginsId: string;
    name: string;
  }>(null);
  const deleteConfirmMethods = useForm({ defaultValues: { confirmText: '' } });
  const linkAccountMethods = useForm<LinkAccountFormValues>({
    defaultValues: linkAccountDefaultValues,
    mode: 'all',
    resolver: zodResolver(
      z.object({
        accountId: string({ required: true }),
        accountTypeId: string({ required: true }),
        isAdmin: z.boolean()
      })
    )
  });

  const linkAccount = usePostAdminAccountsRefLogin<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        invalidate();
        setLinkAccountOpen(false);
        linkAccountMethods.reset(linkAccountDefaultValues);
        notifications.success('Account linked');
      }
    }
  });
  const unlinkAccount = useDeleteAdminAccountsRefLogin<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        invalidate();
        notifications.success('Account unlinked');
      }
    }
  });
  const updateAccountRef = usePatchAdminAccountsRefLogin<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        invalidate();
        notifications.success('Account link updated');
      }
    }
  });

  const accounts = login.data?.AccountsRefLogins ?? [];
  const loginType = getLoginType(accounts);

  const hasParticipantAccount = accounts.some(
    a => a.accountTypeId === ACCOUNT_TYPES.PARTICIPANTS
  );
  const hasAdvisorAccount = accounts.some(
    a => a.accountTypeId === ACCOUNT_TYPES.ADVISORS
  );
  const hasAdminAccount = accounts.some(
    a => a.accountTypeId === ACCOUNT_TYPES.ADMIN
  );

  const planEnrollments = useGetAdminLoginPlanEnrollments(loginId, {
    query: { enabled: hasParticipantAccount }
  });
  const advisoryOrg = useGetAdminLoginAdvisoryOrganization(loginId, {
    query: { enabled: hasAdvisorAccount }
  });

  const onConfirmDisableMfa = () => {
    setDisableMfaOpen(false);
    disableMfa.mutate({ loginId });
  };

  const onConfirmDeleteLogin = () => {
    setDeleteLoginOpen(false);
    deleteConfirmMethods.reset();
    deleteLogin.mutate({ loginId });
  };

  const onDeleteLoginClose = () => {
    setDeleteLoginOpen(false);
    deleteConfirmMethods.reset();
  };

  const onDeleteAdvisor = () => {
    if (!deletingOrg) return;

    deleteAdvisor.mutate({
      advisorId: deletingOrg.advisorId,
      advisoryOrganizationId: deletingOrg.advisoryOrganizationId
    });

    setDeletingOrg(null);
  };

  const onOpenLinkAccount = () => {
    linkAccountMethods.reset(linkAccountDefaultValues);
    setLinkAccountOpen(true);
  };

  const onLinkAccountClose = () => setLinkAccountOpen(false);

  const onLinkAccountSubmit = (data: LinkAccountFormValues) => {
    linkAccount.mutate({
      data: {
        accountId: data.accountId,
        accountTypeId: data.accountTypeId,
        loginId,
        ...(data.accountTypeId === ACCOUNT_TYPES.ADMIN && {
          isAdmin: data.isAdmin
        })
      }
    });
  };

  const onUnlinkClose = () => setUnlinkingAccount(null);

  const onResetEmailClose = () => setResetEmailOpen(false);
  const onResetLinkClose = () => setResetLinkOpen(false);

  const onConfirmResetEmail = () => {
    setResetEmailOpen(false);
    resetPasswordEmail.mutate({ data: { mode: 'email' as const }, loginId });
  };

  const onConfirmUnlink = () => {
    if (!unlinkingAccount) return;

    unlinkAccount.mutate({
      accountsRefLoginsId: unlinkingAccount.accountsRefLoginsId
    });
    setUnlinkingAccount(null);
  };

  const accountColumns: GridColDef[] = [
    {
      field: 'AccountTypes',
      flex: 1,
      headerName: 'Account Type',
      valueGetter: (_value, row) => row.AccountTypes?.description ?? '-'
    },
    {
      field: 'accountName',
      flex: 2,
      headerName: 'Account Name'
    },
    {
      field: 'accountId',
      flex: 1.5,
      headerName: 'Account ID',
      sortable: false
    },
    {
      field: 'isAdmin',
      flex: 0.75,
      headerName: 'Admin',
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'info'}>
          {params.value ? 'Yes' : 'No'}
        </StatusBadge>
      ),
      sortable: false
    },
    {
      field: 'actions',
      flex: 0.5,
      headerName: 'Actions',
      renderCell: params => (
        <ActionMenu
          actions={[
            params.row.accountTypeId === ACCOUNT_TYPES.ADMIN && {
              content: params.row.isAdmin ? 'Revoke Admin' : 'Grant Admin',
              onSelect: () =>
                updateAccountRef.mutate({
                  accountsRefLoginsId: params.row.accountsRefLoginsId,
                  data: { isAdmin: !params.row.isAdmin }
                })
            },
            {
              content: 'Unlink',
              onSelect: () =>
                setUnlinkingAccount({
                  accountsRefLoginsId: params.row.accountsRefLoginsId,
                  name: params.row.accountName ?? params.row.accountId
                })
            }
          ].filter(Boolean)}
          trigger={
            <IconButton
              data-testid={camelCase(
                `accountActions ${params.row.accountName ?? params.row.accountId}`
              )}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false
    }
  ];

  const planEnrollmentColumns: GridColDef[] = [
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name'
    },
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name'
    },
    {
      field: 'planDescription',
      flex: 2,
      headerName: 'Plan'
    },
    {
      field: 'planId',
      flex: 1,
      headerName: 'Plan ID',
      sortable: false
    }
  ];

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link to='/users'>Users</Link>
        <Typography color='text.primary'>
          {login.data?.username ?? '...'}
        </Typography>
      </Breadcrumbs>

      {login.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'
              sx={{ flexWrap: 'wrap', gap: 2 }}>
              <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Username
                  </Typography>
                  <Typography variant='h5'>{login.data.username}</Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    First Name
                  </Typography>
                  <Typography variant='subtitle1'>
                    {login.data.firstName ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Last Name
                  </Typography>
                  <Typography variant='subtitle1'>
                    {login.data.lastName ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Email
                  </Typography>
                  <Typography variant='subtitle1'>
                    {login.data.email ?? '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Created
                  </Typography>
                  <Typography variant='subtitle1'>
                    {login.data.createdAt
                      ? formatDate(login.data.createdAt, {
                          format: 'MM/DD/YYYY'
                        })
                      : '-'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Status
                  </Typography>
                  <div>
                    {isLocked(
                      login.data.failedAttempts,
                      login.data.allowAccessAfter
                    ) ? (
                      <StatusBadge severity='error'>Locked</StatusBadge>
                    ) : (
                      <StatusBadge severity='success'>Active</StatusBadge>
                    )}
                  </div>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    2FA
                  </Typography>
                  <div>
                    <StatusBadge
                      severity={login.data.use2fa ? 'success' : 'warning'}>
                      {login.data.use2fa ? 'Enabled' : 'Disabled'}
                    </StatusBadge>
                  </div>
                </div>
                {hasAdminAccount && (
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Super Admin
                    </Typography>
                    <div>
                      <StatusBadge
                        severity={
                          accounts.find(
                            a => a.accountTypeId === ACCOUNT_TYPES.ADMIN
                          )?.isAdmin
                            ? 'success'
                            : 'info'
                        }>
                        {accounts.find(
                          a => a.accountTypeId === ACCOUNT_TYPES.ADMIN
                        )?.isAdmin
                          ? 'Yes'
                          : 'No'}
                      </StatusBadge>
                    </div>
                  </div>
                )}
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Login ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {loginId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={loginId} />
                  </Stack>
                </div>
              </Stack>

              <ActionMenu
                actions={[
                  {
                    content: 'Delete Login',
                    onSelect: () => setDeleteLoginOpen(true)
                  },
                  !(login.data.use2fa && login.data.isMfaRequired) && {
                    content: login.data.use2fa ? 'Disable MFA' : 'Enable MFA',
                    onSelect: login.data.use2fa
                      ? () => setDisableMfaOpen(true)
                      : () => setEnableMfaOpen(true)
                  },
                  {
                    content: 'Edit Details',
                    onSelect: () => setEditDetailsOpen(true)
                  },
                  {
                    content: 'Resend Invitation',
                    onSelect: () => sendInvitation.mutate({ loginId })
                  },
                  loginType === 'admin' && {
                    content: 'Reset Password',
                    onSelect: () => setResetPasswordOpen(true)
                  },
                  loginType === 'advisor' && {
                    content: 'Send Reset Email',
                    onSelect: () => setResetEmailOpen(true)
                  },
                  loginType === 'advisor' && {
                    content: 'Generate Reset Link',
                    onSelect: () => setResetLinkOpen(true)
                  },
                  isLocked(
                    login.data.failedAttempts,
                    login.data.allowAccessAfter
                  ) && {
                    content: 'Unlock',
                    onSelect: () => unlock.mutate({ loginId })
                  }
                ]
                  .filter(action => !!action)
                  .sort((a, b) => a.content.localeCompare(b.content))}
                trigger={
                  <IconButton data-testid='actionsMenu'>
                    <MoreVertIcon />
                  </IconButton>
                }
              />
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={1}>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'>
          <Typography variant='h6'>Linked Accounts</Typography>
          <Button
            data-testid='linkAccount'
            onClick={onOpenLinkAccount}
            variant='outlined'>
            Link Account
          </Button>
        </Stack>
        <DataGrid
          columns={accountColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.accountsRefLoginsId}
          loading={login.isFetching}
          rows={accounts}
          showColumnMenu
          slotProps={{
            noRowsOverlay: { message: 'No linked accounts' },
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedAccounts'
            }
          }}
        />
      </Stack>

      {hasParticipantAccount && (
        <Stack spacing={1}>
          <Typography variant='h6'>Plan Enrollments</Typography>
          <DataGrid
            columns={planEnrollmentColumns}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={row => row.planEnrollmentId}
            loading={planEnrollments.isFetching}
            rows={planEnrollments.data?.data ?? []}
            showColumnMenu
            slotProps={{
              root: {
                'data-component': 'dataGrid',
                'data-testid': 'planEnrollments'
              }
            }}
          />
        </Stack>
      )}

      {hasAdvisorAccount && (advisoryOrg.data?.data?.length ?? 0) > 0 && (
        <Stack spacing={1}>
          <Typography variant='h6'>Advisory Organization</Typography>
          {advisoryOrg.data?.data?.map(org => (
            <Card key={org.advisoryOrganizationId} sx={{ p: 1 }}>
              <CardContent>
                <Stack
                  alignItems='center'
                  direction='row'
                  justifyContent='space-between'>
                  <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                    <div>
                      <Typography color='text.secondary' variant='overline'>
                        Organization
                      </Typography>
                      <Link
                        data-testid={camelCase(org.name)}
                        params={{
                          advisoryOrganizationId: org.advisoryOrganizationId
                        }}
                        to='/organizations/$advisoryOrganizationId'>
                        <Typography variant='subtitle1'>{org.name}</Typography>
                      </Link>
                    </div>
                    <div>
                      <Typography color='text.secondary' variant='overline'>
                        Advisor
                      </Typography>
                      <Typography variant='subtitle1'>
                        {org.advisorFirstName} {org.advisorLastName}
                      </Typography>
                    </div>
                  </Stack>
                  <ActionMenu
                    actions={[
                      {
                        content: 'Delete',
                        onSelect: () =>
                          setDeletingOrg({
                            advisorId: org.advisorId,
                            advisoryOrganizationId: org.advisoryOrganizationId,
                            name: `${org.advisorFirstName} ${org.advisorLastName}`
                          })
                      },
                      {
                        content: 'Edit',
                        onSelect: () =>
                          navigate({
                            params: {
                              advisoryOrganizationId: org.advisoryOrganizationId
                            },
                            to: '/organizations/$advisoryOrganizationId'
                          })
                      },
                      {
                        content: 'Impersonate',
                        onSelect: () =>
                          window.open(
                            `/api/v3/admin/advisory-organizations/${org.advisoryOrganizationId}/advisors/${org.advisorId}/impersonate`,
                            '_blank'
                          )
                      }
                    ]}
                    trigger={
                      <IconButton
                        data-testid={camelCase(
                          `advisorActions ${org.advisorFirstName} ${org.advisorLastName}`
                        )}>
                        <MoreVertIcon />
                      </IconButton>
                    }
                  />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog
        data-testid='linkAccountDialog'
        fullWidth
        isOpen={linkAccountOpen}
        maxWidth='sm'
        onClose={onLinkAccountClose}>
        <Form
          methods={linkAccountMethods}
          noValidate
          onSubmit={linkAccountMethods.handleSubmit(onLinkAccountSubmit)}>
          <DialogTitle variant='h3'>Link Account</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <SelectInput
                data-testid='linkAccountType'
                hideError
                label='Account Type'
                name='accountTypeId'
                options={accountTypeOptions}
                size='small'
                variant='outlined'
              />
              <Input
                autoComplete='off'
                data-1p-ignore
                data-testid='linkAccountId'
                format='string'
                fullWidth
                hideError
                label='Account ID'
                name='accountId'
                required
                size='small'
              />
              {linkAccountMethods.watch('accountTypeId') ===
                ACCOUNT_TYPES.ADMIN && (
                <SwitchInput
                  color='success'
                  data-testid='linkAccountIsAdmin'
                  hideError
                  label='Admin'
                  name='isAdmin'
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelLinkAccount'
              onClick={onLinkAccountClose}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveLinkAccount'
              disabled={linkAccount.isPending}
              triggerValidation
              variant='contained'>
              Link
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>

      <Dialog
        data-testid='unlinkAccountDialog'
        isOpen={!!unlinkingAccount}
        onClose={onUnlinkClose}>
        <DialogTitle variant='h3'>Unlink Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to unlink account {unlinkingAccount?.name}{' '}
            from this login?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelUnlinkAccount'
            onClick={onUnlinkClose}
            variant='outlined'>
            Cancel
          </Button>
          <Button
            color='error'
            data-testid='confirmUnlinkAccount'
            onClick={onConfirmUnlink}
            variant='contained'>
            Unlink
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        data-testid='deleteAdvisorDialog'
        isOpen={!!deletingOrg}
        onClose={() => setDeletingOrg(null)}>
        <DialogTitle variant='h3'>Delete Advisor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete advisor {deletingOrg?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelDeleteAdvisor'
            onClick={() => setDeletingOrg(null)}
            variant='outlined'>
            Cancel
          </Button>
          <Button
            color='error'
            data-testid='confirmDeleteAdvisor'
            onClick={onDeleteAdvisor}
            variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {hasAdminAccount && (
        <Stack spacing={1}>
          <Typography variant='h6'>Admin Details</Typography>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Stack direction='row' spacing={3} sx={{ flexWrap: 'wrap' }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Role
                  </Typography>
                  <Typography variant='subtitle1'>Administrator</Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Admin Account
                  </Typography>
                  <Typography variant='subtitle1'>
                    {accounts
                      .filter(a => a.accountTypeId === ACCOUNT_TYPES.ADMIN)
                      .map(a => a.accountName ?? a.accountId)
                      .join(', ')}
                  </Typography>
                </div>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {login.data && (
        <>
          {editDetailsOpen && (
            <EditDetailsDialog
              email={login.data.email}
              firstName={login.data.firstName}
              isAdmin={
                accounts.find(a => a.accountTypeId === ACCOUNT_TYPES.ADMIN)
                  ?.isAdmin
              }
              isMfaRequired={login.data.isMfaRequired}
              lastName={login.data.lastName}
              loginId={loginId}
              onClose={() => setEditDetailsOpen(false)}
              onSuccess={invalidate}
              use2fa={login.data.use2fa}
            />
          )}

          <ResetPasswordDialog
            isOpen={resetPasswordOpen}
            loginId={loginId}
            onClose={() => setResetPasswordOpen(false)}
            username={login.data.username}
          />

          {resetLinkOpen && (
            <ResetLinkDialog
              isOpen={resetLinkOpen}
              loginId={loginId}
              onClose={onResetLinkClose}
              username={login.data.username}
            />
          )}

          <ConfirmationDialog
            confirmText='Send Email'
            data-testid='resetEmailDialog'
            isOpen={resetEmailOpen}
            message='Send password reset email to this advisor?'
            onCancel={onResetEmailClose}
            onConfirm={onConfirmResetEmail}
            title='Reset Password'
          />

          {enableMfaOpen && (
            <EnableMfaDialog
              loginId={loginId}
              onClose={() => setEnableMfaOpen(false)}
              onSuccess={invalidate}
            />
          )}

          <Dialog
            data-testid='disableMfaDialog'
            isOpen={disableMfaOpen}
            onClose={() => setDisableMfaOpen(false)}>
            <DialogTitle variant='h3'>Disable MFA</DialogTitle>
            <DialogContent>
              <Typography>
                This will disable two-factor authentication for this user.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                data-testid='cancelDisableMfa'
                onClick={() => setDisableMfaOpen(false)}
                variant='outlined'>
                Cancel
              </Button>
              <Button
                data-testid='confirmDisableMfa'
                onClick={onConfirmDisableMfa}
                variant='contained'>
                Disable
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            data-testid='deleteLoginDialog'
            isOpen={deleteLoginOpen}
            onClose={onDeleteLoginClose}>
            <Form methods={deleteConfirmMethods}>
              <DialogTitle variant='h3'>Delete Login</DialogTitle>
              <DialogContent>
                <Typography sx={{ mb: 2 }}>
                  Type &quot;{login.data.username}&quot; to confirm deleting
                  this login and all linked accounts.
                </Typography>
                <Input
                  autoComplete='off'
                  data-1p-ignore
                  data-testid='deleteConfirmInput'
                  format='string'
                  fullWidth
                  label={`Type '${login.data.username}' to confirm`}
                  name='confirmText'
                  size='small'
                />
              </DialogContent>
              <DialogActions>
                <Button
                  data-testid='cancelDeleteLogin'
                  onClick={onDeleteLoginClose}
                  variant='outlined'>
                  Cancel
                </Button>
                <Button
                  color='error'
                  data-testid='confirmDeleteLogin'
                  disabled={
                    deleteConfirmMethods.watch('confirmText') !==
                    login.data.username
                  }
                  onClick={onConfirmDeleteLogin}
                  variant='contained'>
                  Delete
                </Button>
              </DialogActions>
            </Form>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const Route = createFileRoute('/users/$loginId')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: UserDetailPage
});
